/**
 * Shared toggle switch — the one switch idiom across OpenDork.
 * Monochrome by default; amber reserved for Grounded/warning semantics.
 */
import { cn } from '@/lib/utils';

interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Accessible name (required — no visible label built in) */
  label: string;
  disabled?: boolean;
  /** 'default' = warm white track; 'amber' = grounded/warning accent */
  accent?: 'default' | 'amber';
  /** Stop click propagation (use inside clickable rows) — default true */
  stopPropagation?: boolean;
  className?: string;
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
  accent = 'default',
  stopPropagation = true,
  className,
}: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        onChange(!checked);
      }}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors duration-200',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        checked
          ? accent === 'amber'
            ? 'bg-amber-400/85 border-amber-300/40'
            : 'bg-[#e8e6e3] border-[#e8e6e3]'
          : 'bg-[#161616] border-[#2a2a2a] hover:border-[#3a3a3a]',
        className,
      )}
    >
      <span
        className={cn(
          'inline-block h-3.5 w-3.5 rounded-full shadow-sm transition-transform duration-200',
          checked
            ? 'translate-x-[17px] bg-[#0a0a0a]'
            : 'translate-x-[3px] bg-[#5a5a5a]',
        )}
      />
    </button>
  );
}
